/**
 * PlanCreatedCard - Displays multi-task execution plan
 * 
 * Shows the goal, sub-tasks, repositories, and reasoning.
 */
import { cn } from '@/lib/utils'
import { ClipboardCheck, ExternalLink } from 'lucide-react'

export interface PlanSubTask {
  order: number
  description: string
  repository?: string
}

export interface PlanRepository {
  url: string
  relevance: string
}

export interface PlanCreatedData {
  goal: string
  sub_tasks: Array<PlanSubTask>
  repositories?: Array<PlanRepository>
  derived_constraints?: Array<string>
}

export interface PlanCreatedCardProps {
  data: PlanCreatedData
  reasoning?: string
  className?: string
}

export function PlanCreatedCard({ data, reasoning, className }: PlanCreatedCardProps) {
  const { goal, sub_tasks, repositories } = data
  
  return (
    <div className={cn("rounded-lg border bg-card p-4 space-y-4", className)}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-full bg-primary/10">
          <ClipboardCheck className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base">Plan Created</h3>
          <p className="text-sm text-muted-foreground mt-1">{goal}</p>
        </div>
      </div>
      
      {/* Sub-tasks */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-muted-foreground">Sub-tasks ({sub_tasks.length})</h4>
        <ol className="space-y-2">
          {sub_tasks.map((task, idx) => (
            <li key={idx} className="flex gap-3 p-2 rounded bg-muted/50">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-medium flex items-center justify-center">
                {task.order || idx + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm">{task.description}</p>
                {task.repository && (
                  <p className="text-xs text-muted-foreground mt-1">Repository: {task.repository}</p>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>
      
      {/* Repositories */}
      {repositories && repositories.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Repositories</h4>
          <ul className="space-y-1">
            {repositories.filter(repo => repo?.url).map((repo, idx) => (
              <li key={idx} className="text-sm flex items-start gap-2">
                <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div>
                  <a 
                    href={repo.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-primary hover:underline"
                  >
                    {repo.url.split('/').slice(-2).join('/')}
                  </a>
                  {repo.relevance && (
                    <span className="text-muted-foreground ml-2">- {repo.relevance}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Reasoning */}
      {reasoning && (
        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground">{reasoning}</p>
        </div>
      )}
    </div>
  )
}
